import { useToolStore } from '../../stores/toolStore';
import type { ViewportState } from '../../lib/spine-manager';

const RULER_SIZE = 20;

const ASPECT_RATIOS: Record<string, number> = {
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '4:3':  4 / 3,
  '1:1':  1,
};

interface SafeZoneOverlayProps {
  canvasWidth: number;
  canvasHeight: number;
  vpState: ViewportState;
}

export default function SafeZoneOverlay({ canvasWidth, canvasHeight, vpState }: SafeZoneOverlayProps) {
  const safeZoneAspect = useToolStore((s) => s.safeZoneAspect);
  if (!safeZoneAspect) return null;

  const targetRatio = ASPECT_RATIOS[safeZoneAspect];
  const { zoom, x: vpX, y: vpY } = vpState;

  // Artboard screen rect (in outer container coords, including ruler inset).
  // The artboard is centered at world origin (0,0); vpX/vpY are where world-origin
  // maps to PixiJS stage space, so the artboard top-left is offset by half its size.
  const artWidth  = canvasWidth  * zoom;
  const artHeight = canvasHeight * zoom;
  const artLeft   = RULER_SIZE + vpX - artWidth  / 2;
  const artTop    = RULER_SIZE + vpY - artHeight / 2;

  // Safe zone rectangle, centered inside artboard, matching targetRatio
  const artRatio = artWidth / artHeight;
  let szWidth: number, szHeight: number;
  if (artRatio > targetRatio) {
    szHeight = artHeight;
    szWidth  = szHeight * targetRatio;
  } else {
    szWidth  = artWidth;
    szHeight = szWidth / targetRatio;
  }

  const szLeft = artLeft + (artWidth  - szWidth)  / 2;
  const szTop  = artTop  + (artHeight - szHeight) / 2;

  return (
    <>
      {/* Dark overlay outside the safe zone (4 rects: top, bottom, left, right) */}
      {/* Top strip */}
      <div
        className="absolute pointer-events-none z-20"
        style={{ left: artLeft, top: artTop, width: artWidth, height: szTop - artTop, backgroundColor: 'rgba(0,0,0,0.4)' }}
      />
      {/* Bottom strip */}
      <div
        className="absolute pointer-events-none z-20"
        style={{ left: artLeft, top: szTop + szHeight, width: artWidth, height: artTop + artHeight - (szTop + szHeight), backgroundColor: 'rgba(0,0,0,0.4)' }}
      />
      {/* Left strip */}
      <div
        className="absolute pointer-events-none z-20"
        style={{ left: artLeft, top: szTop, width: szLeft - artLeft, height: szHeight, backgroundColor: 'rgba(0,0,0,0.4)' }}
      />
      {/* Right strip */}
      <div
        className="absolute pointer-events-none z-20"
        style={{ left: szLeft + szWidth, top: szTop, width: artLeft + artWidth - (szLeft + szWidth), height: szHeight, backgroundColor: 'rgba(0,0,0,0.4)' }}
      />

      {/* Safe zone border */}
      <div
        data-testid="safe-zone-overlay"
        className="absolute pointer-events-none z-20 border-2 border-accent/70"
        style={{ left: szLeft, top: szTop, width: szWidth, height: szHeight }}
      >
        {/* Label */}
        <div className="absolute -top-5 left-0 text-[10px] font-mono text-accent/80 bg-bg-surface/80 px-1 rounded-sm">
          {safeZoneAspect}
        </div>
      </div>
    </>
  );
}
